import { Stack } from '@chakra-ui/react';
import { Vehicle } from 'api/types';
import Link from 'components/Link';
import { useUserContext } from 'context/UserContextProvider';
import { isEqual, isNil, keys } from 'lodash';
import { Dispatch, useMemo } from 'react';
import { SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IdentificationResultIcon } from '../../components/Icons';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { StatusTag } from '../../components/StatusTag';
import { DataTable } from '../../components/Table/DataTable';
import { SelectColumnFilter } from '../../components/Table/Filter';
import { dateTransformerNoTimezone } from '../../helpers/dateTransformer';
import { createDropdownPlainOptions, statusFilter } from '../../helpers/general';
import useServiceRecordsQuery from '../../hooks/useServiceRecordsQuery';
import { ContentCard } from '../../layout/ContentCard';

const WorkshopServiceRecordOverviewCard = (props: { workshopId: number; autoFocus?: boolean; setFocusInput?: Dispatch<SetStateAction<boolean>> }) => {
  const { workshopId, autoFocus, setFocusInput } = props;
  const { t } = useTranslation();
  const { statusCodes, makes } = useUserContext();
  const serviceRecordStatusCodes = statusCodes?.service_record || {};
  const initialStatus = keys(serviceRecordStatusCodes).filter((code) => !['0'].includes(code));
  const initialFilters = {
    searchString: '',
    itemsPerPage: 10,
    status: initialStatus || [],
    assignee: undefined,
    make: undefined,
    fastlane: undefined,
    language: undefined,
  };
  const localStorageData = window.localStorage.getItem('workshop_service_records_filters');
  // @ts-ignore
  const destringifiedData = JSON.parse(localStorageData) || {};
  useEffect(() => {
    if (!localStorageData) {
      window.localStorage.setItem('workshop_service_records_filters', JSON.stringify(initialFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchString, setSearchString] = useState(destringifiedData?.searchString || initialFilters?.searchString);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(destringifiedData?.itemsPerPage || initialFilters?.itemsPerPage);
  const [status, setStatus] = useState(destringifiedData?.status || initialFilters?.status);
  const [make, setMake] = useState(destringifiedData?.make || initialFilters?.make);

  const [language] = useState(destringifiedData?.language || initialFilters?.language);

  const [assignee, setAssignee] = useState(destringifiedData?.assignee || initialFilters?.assignee);
  const [fastlane] = useState(destringifiedData?.fastlane || initialFilters?.fastlane);

  const serviceRecordsQuery = useServiceRecordsQuery({
    searchString,
    page,
    itemsPerPage,
    status,
    make,
    language,
    assignee,
    fastlane,
    workshopId,
  });

  useEffect(() => {
    setStatus(initialStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusCodes]);
  const { opUsers } = useUserContext();

  const initialLoad = searchString === '' && itemsPerPage === 10 && page === 1 && isEqual(initialStatus, status) && isNil(assignee) && isNil(make);

  useEffect(() => {
    setPage(1);
  }, [searchString, itemsPerPage]);

  const allStatusSelected = status.length === Object.keys(serviceRecordStatusCodes).length;

  useEffect(() => {
    window.localStorage.setItem(
      'workshop_service_records_filters',
      JSON.stringify({
        searchString: searchString,
        status: status,
        assignee: assignee,
        make: make,
        itemsPerPage: itemsPerPage,
      }),
    );
  }, [searchString, status, assignee, make, itemsPerPage]);

  const columns = useMemo(
    () => [
      {
        Header: t('forms:id.label'),
        accessor: 'user_readable_id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return (
            <Link data-test-id={'sr-' + row?.index} to={`/app/op/service-record/${row.original.id}`}>
              {row.original.user_readable_id}
            </Link>
          );
        },
      },
      {
        Header: t('forms:status.label'),
        accessor: 'status',
        shouldFilter: true,
        Filter: () => statusFilter(status, setStatus, 'service_record', serviceRecordStatusCodes, allStatusSelected),
        // @ts-ignore
        Cell: ({ value }) => <StatusTag resourceKey={'service_record'} currentStatus={value} />,
      },
      {
        Header: t('forms:assignee.label'),
        accessor: 'open_task.assignee.name',
        // @ts-ignore
        Cell: (props) => {
          return props.value ?? '-';
        },
        shouldFilter: true,
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: assignee,
            column,
            dropdownOptions: createDropdownPlainOptions({
              // @ts-ignore
              records: opUsers,
              valueColumn: 'id',
              labelColumn: 'name',
            }),
            onChange: setAssignee,
          }),
      },
      {
        Header: t('forms:submitted_at.label', { date: '' }),
        accessor: (row: { submitted_at: string }) => dateTransformerNoTimezone(row.submitted_at) ?? '-',
      },
      {
        Header: t('forms:vin.label'),
        accessor: (row: { vehicle: Vehicle }) => row.vehicle?.vin,
      },
      {
        Header: t('forms:make.label'),
        accessor: (row: { vehicle: Vehicle }) => row.vehicle?.manufacturer?.make?.name,
        shouldFilter: true,
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: make,
            column,
            dropdownOptions: createDropdownPlainOptions({
              // @ts-ignore
              records: makes,
              labelColumn: 'name',
              valueColumn: 'id',
            }),
            onChange: setMake,
          }),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serviceRecordStatusCodes, searchString, status, assignee, make, language, fastlane, serviceRecordsQuery.data],
  );

  if (serviceRecordsQuery.isLoading) {
    return <LoadingSpinner />;
  }
  return (
    <ContentCard
      isLoading={serviceRecordsQuery.isFetching}
      icon={<IdentificationResultIcon boxSize={10} />}
      minH="fit-content"
      header={t('service_record:header')}
      data-test-id="service-records-overview-card"
    >
      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w={'100%'}>
          <DataTable
            resetFilter={() => {
              setSearchString(initialFilters?.searchString);
              setPage(1);
              setStatus(initialFilters?.status);
              setAssignee(initialFilters?.assignee);
              setItemsPerPage(initialFilters?.itemsPerPage);
              setMake(initialFilters?.make);
              window.localStorage.setItem('workshop_service_records_filters', JSON.stringify(initialFilters));
            }}
            data-test-id="workshop-service-records-table"
            // @ts-ignore
            data={serviceRecordsQuery.data?.data}
            globalFilter={searchString}
            // @ts-ignore
            setGlobalFilter={setSearchString}
            autoFocus={autoFocus}
            setFocusInput={setFocusInput}
            globalFilterPlaceholder={t('service_record:search', 'Name, Email, Kunden Id ...')}
            // @ts-ignore
            hasFilters={true}
            // @ts-ignore
            currentPage={serviceRecordsQuery.data?.meta?.current_page - 1}
            perPage={itemsPerPage}
            // @ts-ignore
            total={serviceRecordsQuery.data?.meta?.last_page}
            columns={columns}
            setPage={setPage}
            setPerPage={setItemsPerPage}
            loading={serviceRecordsQuery.isFetching}
            filtersInitiallyOpen
            isMoreThenOnePage={
              // @ts-ignore
              !initialLoad || Boolean(serviceRecordsQuery.data?.next_page_url)
            }
          />
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default WorkshopServiceRecordOverviewCard;
